import { Link } from 'react-router-dom';
import { IconDropdownArrow, IconNoteOnPaper } from 'assets/DesignSystem/SVGComponents';
import BordOneToneIcon from 'components/NudosComponents/BordOneToneIcon/BordOneToneIcon';
import FlagImageByUrl from 'components/FlagImageByUrl/FlagImageByUrl';
import { IShipmentGroupedByOrder } from 'types/orders';
import ShipmentItem from '../ShipmentItem/ShipmentItem';
import ShipmentDetailDrawer from '../ShipmentDetailDrawer/ShipmentDetailDrawer';
import PreparationChecklistDrawer from '../PreparationChecklistDrawer/PreparationChecklistDrawer';
import CheckOrAddCommentsModal from '../../../orders/Components/CheckOrAddCommentsModal/CheckOrAddCommentsModal';
import useOperationsCard from './useOperationsCard';
import { nudosBlue } from 'styles/styles.constants';
import './OperationsCard.scss';

interface OperationsCardProps {
  data: IShipmentGroupedByOrder;
  onCountryClick?: (countryId: number | null, countryName?: string) => void;
  onOrganizationClick?: (organizationName: string | null) => void;
  onRefetch?: () => void;
  onFocusOrder?: (orderId: number | null) => void;
  focusedOrderId?: number | null;
  focusedCountryNames?: string[];
  focusedOrganizationName?: string | null;
}

const OperationsCard = ({
  data,
  onCountryClick,
  onOrganizationClick,
  onRefetch,
  onFocusOrder,
  focusedOrderId,
  focusedCountryNames,
  focusedOrganizationName
}: OperationsCardProps) => {
  const {
    showCommentsModal,
    isShipmentDrawerOpen,
    isPreparationDrawerOpen,
    selectedShipmentId,
    isLoadingComments,
    orderNumber,
    companyName,
    countryFlagUrl,
    countryName,
    countryId,
    shipmentsCount,
    orderComments,
    showFocusButton,
    focusIconColor,
    slaDate,
    hasMoreShipments,
    hasTooManyShipments,
    visibleShipmentGroups,
    cardClassNames,
    orderDetailUrl,
    isFocused,
    isCountryFocused,
    isOrganizationFocused,
    isShipmentsExpanded,
    shipmentsInCardCount,
    handleToggleShipments,
    handleFocusOrder,
    handleCountryClick,
    handleOrganizationClick,
    handleOpenOrCloseCommentsModal,
    handleCommentsSubmission,
    selectedShipmentIds,
    handleShipmentClick,
    handleCloseShipmentDrawer,
    handlePreparationClick,
    handleClosePreparationDrawer,
    handleChecklistCountersChange,
    isPreparationDrawerOpenForShipment
  } = useOperationsCard({
    data,
    onRefetch,
    onFocusOrder,
    focusedOrderId,
    onCountryClick,
    focusedCountryNames,
    onOrganizationClick,
    focusedOrganizationName
  });

  return (
    <div className={cardClassNames}>
      <div className="wrapper">
        <div className="leftSection">
          <div className="orderNumberWrapper">
            <Link to={orderDetailUrl} className="orderNumber">
              Nº{orderNumber}
            </Link>
            {showFocusButton && (
              <button onClick={handleFocusOrder}>
                <BordOneToneIcon
                  variant="gps"
                  standardSize={12}
                  stroke={focusIconColor}
                  fill={isFocused ? nudosBlue : 'transparent'}
                />
              </button>
            )}
          </div>
          <div className="companyInfo">
            <button
              type="button"
              className={`countryFlagButton ${isCountryFocused ? 'countryFocused' : ''}`}
              onClick={handleCountryClick}
              disabled={!countryId}
              aria-label={`Filtrar por país ${countryName}`}
            >
              <FlagImageByUrl url={countryFlagUrl} className="countryFlagImage" />
            </button>
            <button
              className={`companyNameButton ${isOrganizationFocused ? 'organizationFocused' : ''}`}
              onClick={handleOrganizationClick}
              aria-label={`Filtrar por empresa ${companyName}`}
              title={companyName}
            >
              <span className="companyName">{companyName}</span>
            </button>
          </div>
        </div>
        <div className="rightSection">
          <span className="sla">
            <b>SLA:</b> {slaDate}
          </span>
          <button
            className="openOrderCommentsBtn"
            onClick={handleOpenOrCloseCommentsModal}
            aria-label="Abrir comentarios de la orden"
          >
            <IconNoteOnPaper />
          </button>
        </div>
      </div>

      <div className="divider" />
      <div className="shipmentSection">
        {visibleShipmentGroups.map(({ shipment, group }, index) => {
          const firstShipmentId = group[0].id;
          const shipmentIds = group.map(s => s.id);
          return (
            <ShipmentItem
              key={`shipment-group-${firstShipmentId}-${index}`}
              shipment={shipment}
              groupSize={group.length}
              currentShipmentNumber={index + 1}
              totalShipmentsNumber={shipmentsInCardCount}
              isPreparationChecklistDrawerOpen={isPreparationDrawerOpenForShipment(firstShipmentId)}
              onClick={() => handleShipmentClick(shipmentIds, index)}
              onPreparationClick={() => handlePreparationClick(firstShipmentId, index)}
            />
          );
        })}
      </div>

      {hasMoreShipments && !hasTooManyShipments && (
        <div className="expandShipmentsBtnWrapper">
          <button className="expandShipmentsBtn" onClick={handleToggleShipments}>
            <span>
              {isShipmentsExpanded ? 'Ver menos' : 'Ver más'} ({shipmentsInCardCount})
            </span>
            <IconDropdownArrow className={isShipmentsExpanded ? 'rotated' : ''} width={14} height={14} />
          </button>
        </div>
      )}

      {hasTooManyShipments && (
        <div className="tooManyShipmentsMessage">
          <span>
            La orden tiene <b>({shipmentsCount}) envíos.</b> Ver todos desde la interna.
          </span>
        </div>
      )}

      {showCommentsModal && (
        <CheckOrAddCommentsModal
          closeModal={handleOpenOrCloseCommentsModal}
          orderComments={orderComments}
          handleSubmitComments={handleCommentsSubmission}
          loading={isLoadingComments}
          isLoadingInitialComments={isLoadingComments}
        />
      )}

      <ShipmentDetailDrawer
        isOpen={isShipmentDrawerOpen}
        onClose={handleCloseShipmentDrawer}
        orderId={data?.orderId}
        shipmentIds={selectedShipmentIds.length > 0 ? selectedShipmentIds : undefined}
      />

      <PreparationChecklistDrawer
        isOpen={isPreparationDrawerOpen}
        onClose={handleClosePreparationDrawer}
        orderId={data?.orderId}
        shipmentId={selectedShipmentId || undefined}
        companyName={companyName}
        companyFlagUrl={countryFlagUrl}
        onChecklistCountersChange={handleChecklistCountersChange}
      />
    </div>
  );
};

export default OperationsCard;
